import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Allergen } from '@sg/graphql-schema';

import type { Asset } from 'contentful';

/**
 * Returns the contentful data for dietary preferences.
 */
export const useDietaryPreferencesContent = (content: DietaryPreferencesContent | undefined) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const title = content?.title ?? formatMessage(fallbacks.title);
    const subtitle = content?.subtitle ?? formatMessage(fallbacks.subtitle);
    const disclaimer = content?.disclaimer ?? formatMessage(fallbacks.disclaimer);
    const confirmCta = content?.confirmCta ?? formatMessage(fallbacks.confirmCta);

    return {
      title,
      subtitle,
      disclaimer,
      confirmCta,
      icons: {
        crustaceanShellfish: content?.iconCrustaceanShellfish?.fields?.file?.url,
        milk: content?.iconMilk?.fields?.file?.url,
        eggs: content?.iconEggs?.fields?.file?.url,
        fish: content?.iconFish?.fields?.file?.url,
        meat: content?.iconMeat?.fields?.file?.url,
        soybeans: content?.iconSoybeans?.fields?.file?.url,
        peanuts: content?.iconPeanuts?.fields?.file?.url,
        treeNuts: content?.iconTreeNuts?.fields?.file?.url,
        sesame: content?.iconSesame?.fields?.file?.url,
        wheat: content?.iconWheat?.fields?.file?.url,
      },
    };
  }, [content, formatMessage]);
};

/**
 * Given an allergen, returns the icon from the contentful data.
 */
export const getIconForAllergen = (allergen: Allergen, icons: Content['icons']) => {
  switch (allergen) {
    case Allergen.CrustaceanShellfish:
      return icons.crustaceanShellfish;
    case Allergen.Milk:
      return icons.milk;
    case Allergen.Eggs:
      return icons.eggs;
    case Allergen.Fish:
      return icons.fish;
    case Allergen.Meat:
      return icons.meat;
    case Allergen.Soybeans:
      return icons.soybeans;
    case Allergen.Peanuts:
      return icons.peanuts;
    case Allergen.TreeNuts:
      return icons.treeNuts;
    case Allergen.Sesame:
      return icons.sesame;
    case Allergen.Wheat:
      return icons.wheat;
  }
};

// ─── Fallbacks ───────────────────────────────────────────────────────────────

const fallbacks = defineMessages({
  title: {
    defaultMessage: 'Allergen info and dietary preferences',
    description: 'Dietary Preferences | Fallback | Title',
  },
  subtitle: {
    defaultMessage: `Select the items you can't or don't want to eat. Items that contain these properties will be flagged.*`,
    description: 'Dietary Preferences | Fallback | Subtitle',
  },
  disclaimer: {
    defaultMessage: `* At sweetgreen we use all major allergens in our kitchens, so we cannot guarantee that our food is completely free of any allergen. If you have a severe allergy, we recommend not ordering from our restaurant.

Please note that your online dietary preferences are not communicated to our in-store teams.`,
    description: 'Dietary Preferences | Fallback | Disclaimer',
  },
  confirmCta: {
    defaultMessage: 'Confirm preferences',
    description: 'Dietary Preferences | Fallback | Confirm CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type DietaryPreferencesContent = {
  title: string;
  subtitle: string;
  disclaimer: string;
  confirmCta: string;
  iconCrustaceanShellfish: Asset;
  iconMilk: Asset;
  iconEggs: Asset;
  iconFish: Asset;
  iconMeat: Asset;
  iconSoybeans: Asset;
  iconPeanuts: Asset;
  iconTreeNuts: Asset;
  iconSesame: Asset;
  iconWheat: Asset;
};

type Content = ReturnType<typeof useDietaryPreferencesContent>;
